import React, { useEffect, useRef, useState } from "react";
import "quill/dist/quill.snow.css";
import { extractNodesFromHtml } from "components/Resume/ResumePDF/common";
import { selectIsReset } from "lib/redux/settingsSlice";
import { useAppSelector } from "lib/redux/hooks";

// Import Quill dynamically
const loadQuill = async () => {
  if (typeof window !== "undefined") {
    const { default: Quill } = await import("quill");
    return Quill;
  }
  return null;
};

interface RichEditorProps<K extends string, V extends string[]> {
  name: K;
  value?: V;
  onChange: (field: K, value: V) => void;
  labelClassName?: string;
  placeholder?: string;
}

const RichEditor = <T extends string>({
  name,
  value,
  onChange,
  labelClassName,
  placeholder,
}: RichEditorProps<T, string[]>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<any>(null);
  const [Quill, setQuill] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const isResumeReset = useAppSelector(selectIsReset);
  
  // Track if the change originated from Quill itself (typing) vs external (reorder/reset)
  const isInternalChange = useRef(false);
  // Store the last value we set into Quill to detect external changes
  const lastSetValue = useRef<string[]>([]);

  useEffect(() => {
    // Load Quill when the component mounts
    const initializeQuill = async () => {
      setLoading(true);
      const loadedQuill = await loadQuill();
      setQuill(() => loadedQuill);
      setLoading(false);
    };

    initializeQuill();
  }, []); // Empty dependency array ensures this runs only once

  useEffect(() => {
    if (Quill && containerRef.current) {
      const editorContainer = document.createElement("div");
      containerRef.current.appendChild(editorContainer);

      const quillInstance = new Quill(editorContainer, {
        theme: "snow",
        placeholder: placeholder,
        modules: {
          toolbar: [
            [{ list: "bullet" }],
            ["bold", "italic", "underline"],
            ["link"],
          ],
        },
        bounds: containerRef.current,
      });

      quillRef.current = quillInstance;

      // Set initial content
      if (value && value.length > 0) {
        // Convert array of strings to HTML with each string in separate <p> tags
        const htmlContent = `<ol>${value
          .map(
            (line) =>
              `<li data-list="bullet"><span class="ql-ui" contenteditable="false"></span>${line}</li>`
          )
          .join("")}</ol>`;

        quillInstance.root.innerHTML = htmlContent;
        lastSetValue.current = [...value];
      }

      // Handle text change
      const handleTextChange = () => {
        // Mark that this change came from Quill (user typing)
        isInternalChange.current = true;
        
        const htmlToBeParsed = quillInstance.root.innerHTML;
        const htmlNodes = extractNodesFromHtml(htmlToBeParsed);
        // filter and remove empty nodes
        const filteredNodes = htmlNodes.filter((node: Node) => {
          const element = node as HTMLElement;
          return element.innerText !== "";
        });
        const newFilteredContent = filteredNodes.map((node: Node) => {
          // Change filteredContent to array of strings conatining html content
          const element = node as HTMLElement;
          return element.outerHTML;
        });
        
        // Update our tracking ref
        lastSetValue.current = newFilteredContent;
        onChange(name, newFilteredContent);
        
        // Reset the flag after a brief delay to allow Redux update to complete
        setTimeout(() => {
          isInternalChange.current = false;
        }, 0);
      };

      quillInstance.on("text-change", handleTextChange);

      return () => {
        containerRef.current?.removeChild(editorContainer);
        quillRef.current = null;
      };
    }
  }, [Quill]); // Don't include value here - we handle it separately below

  // Handle external value updates (reordering, reset) without breaking cursor
  useEffect(() => {
    if (!quillRef.current || !value) return;
    
    // Skip update if this change came from Quill itself (user typing)
    if (isInternalChange.current) {
      return;
    }
    
    // Check if value actually changed from what we last set
    // This prevents unnecessary updates when Redux round-trips the same data
    const valueChanged = JSON.stringify(value) !== JSON.stringify(lastSetValue.current);
    
    if (valueChanged && value.length > 0) {
      // This is an external change (reorder or reset) - update Quill
      const htmlContent = `<ol>${value
        .map(
          (line) =>
            `<li data-list="bullet"><span class="ql-ui" contenteditable="false"></span>${line}</li>`
        )
        .join("")}</ol>`;
      
      // Save cursor position before update
      const selection = quillRef.current.getSelection();
      
      // Update content
      quillRef.current.root.innerHTML = htmlContent;
      lastSetValue.current = [...value];
      
      // Restore cursor if it was in the editor
      if (selection) {
        try {
          quillRef.current.setSelection(selection);
        } catch (e) {
          // If restoration fails, just place cursor at end
          const length = quillRef.current.getLength();
          quillRef.current.setSelection(length - 1);
        }
      }
    }
  }, [value, isResumeReset]);

  return (
    <div className={labelClassName}>
      <label
        className={`text-[10px] font-medium text-gray-700 lg:text-xs xl:text-base`}
      >
        Description
      </label>
      <div className="bg-white rounded-3xl" ref={containerRef}></div>
    </div>
  );
};

export default RichEditor;